<template>
  <FormKit
    v-bind="bindings"
    v-model="comments"
    type="textarea"
    inputClass="resize-none"
    :outerClass="'md:max-w-md pb-6 ' + bindings.outerClass"
    wrapperClass="flex-col"
    labelClass="w-full mb-1 text-racq-dark-grey text-base-bold"
    :suffixIcon="comments.length > 0 ? CLEAR_ICON : ''"
    suffixIconClass="cursor-pointer h-fit px-[5px] pt-[9px] pb-[4px]"
    :maxlength="`${commentsMaxLength}`"
    :help="`${comments ? commentsMaxLength - comments.length : commentsMaxLength} characters remaining`"
    :validation="mergedValidation"
    :validationMessages="{
      length: `Comments cannot be more than ${commentsMaxLength} characters.`,
      ...bindings.validationMessages!,
    }"
    @suffixIconClick="clearComments()"
    @paste.prevent="handlePaste"
  />
</template>

<script lang="ts" setup>
import { ref, nextTick, computed } from 'vue';

import { VueFormInputProps } from '@/composables/useFormInputData';
import { Emoji_Regex } from '@/utils/regex-helpers';
import { CLEAR_ICON } from '@/constants/svg-images.const';

// Define props and emitters
// ------------------------------------------------------------- //
const props = defineProps<{
  bindings: VueFormInputProps;
  maxContentLength?: number;
}>();

// Define Composables
// ------------------------------------------------------------- //
const commentsMaxLength = props.maxContentLength ?? 240; //Limit comments to 240 characters if not provided

// Define Component state
// ------------------------------------------------------------- //
const comments = ref<string>('');
type RuleTuple = [rule: string, ...args: any[]];

const mergedValidation = computed<RuleTuple[]>(() => {
  const base: RuleTuple[] = Array.isArray(props.bindings?.validation) ? (props.bindings.validation as RuleTuple[]) : []; // if it’s a string, don’t mix—handle with option 2 instead

  return [...base, ['length', 0, commentsMaxLength]];
});

function handlePaste(event: ClipboardEvent) {
  // Regex to capture emojis (find below the breakdown of the expression)
  // 05/09/2024 - This is the closest to capture all of the emojis in a single expression; however it may not be fool proof.
  /*
    \p{Emoji_Presentation} --> matches characters that are presented as emojis by default.
    \uFE0F --> the variation selector-16 (VS16), which forces preceding characters to be displayed as emojis.
    \u200D --> the zero width jointer (ZWJ) used in combining characters to create complex emojis.
    u --> flag at the end to enable full Unicode matching
    g --> flag at the end for matching all occurances in the string
  */
  const clipboardData = event.clipboardData;

  if (!clipboardData) return;

  // Get the pasted data as text from the clipboard
  let pastedData = clipboardData.getData('text');

  // Remove the emojis from the pasted text
  pastedData = pastedData.replace(Emoji_Regex, '');

  const target = event.target as HTMLTextAreaElement | null;

  if (target) {
    const start = target.selectionStart;
    const end = target.selectionEnd;

    const currentComments = comments.value;
    const newCommentsLength = currentComments.length - (end - start) + pastedData.length;

    // Trim pasted text if it exceeds commentsMaxLength
    if (newCommentsLength > commentsMaxLength) {
      const availableSpace = commentsMaxLength - (currentComments.length - (end - start));
      pastedData = pastedData.slice(0, availableSpace);
    }

    // Update the comments and the store value
    comments.value = currentComments.slice(0, start) + pastedData + currentComments.slice(end);

    // Move the cursor to the end of the pasted text
    nextTick(() => {
      target.setSelectionRange(start + pastedData.length, start + pastedData.length);
    });
  }
}

function clearComments() {
  comments.value = '';
}
</script>
